import { useQuery } from "@apollo/client/react";
import { Box, IconicButton, Text } from "@nulogy/components";
import DeleteConfirmationModal from "components/common/DeleteConfirmationModal";
import { GridBox4 } from "components/common/EditableListStyledComponents";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import { removeTeardownTime } from "hooks/apollo/teardownTime/helpers/handleUpdateTeardownTimes";
import { GetTeardownTimes } from "hooks/apollo/teardownTime/teardownTime.gql";
import useDeleteTeardownTime from "hooks/apollo/teardownTime/useDeleteTeardownTime";
import useDeleteConfirmationModal from "hooks/state/useDeleteConfirmationModal";
import isEmpty from "lodash/isEmpty";
import React, { useState } from "react";
import Uuid from "utils/Uuid";
import CreateNewTeardownTime from "./CreateNewTeardownTime";
import TeardownTime from "./TeardownTime";

export default function TeardownTimes() {
  const { data, loading } = useQuery(GetTeardownTimes);
  const [newTeardownTimeIds, setNewTeardownTimeIds] = useState([]);
  const { open: openModal } = useDeleteConfirmationModal();
  const deleteTeardownTime = useDeleteTeardownTime();

  const handleAddNewTeardownTimeId = () => {
    setNewTeardownTimeIds([...newTeardownTimeIds, Uuid.generate()]);
  };

  const handleRemoveNewTeardownTimeId = (id) => {
    setNewTeardownTimeIds(newTeardownTimeIds.filter((value) => value !== id));
  };

  const handleDelete = (teardownTimeId) => {
    openModal(teardownTimeId);
  };

  const onDelete = (teardownTimeId) =>
    deleteTeardownTime({
      update: (proxy) => {
        removeTeardownTime(teardownTimeId, proxy);
      },
      variables: { id: teardownTimeId },
    });

  function renderContent() {
    if (loading) return null;

    const showTeardownTimes = !isEmpty(data.teardownTimes) || !isEmpty(newTeardownTimeIds);

    const addButtonText = showTeardownTimes ? "Add another row" : "Add a teardown time";
    const deleteMessage =
      "This teardown time will not be available for scheduling once deleted. However, blocks that it is already applied to will not be impacted.";

    return (
      <Box width="870px">
        <DeleteConfirmationModal message={deleteMessage} onDelete={onDelete} title="Delete teardown time" />
        <IconicButton
          className="spec-add-new-teardown-time-icon"
          data-testid="add-teardown-time-button"
          icon="add"
          marginBottom="x2"
          onClick={() => handleAddNewTeardownTimeId()}
          type="button"
        >
          {addButtonText}
        </IconicButton>
        {showTeardownTimes && (
          <>
            <GridBox4>
              <Text>Label</Text>
              <Text>Duration (hours)</Text>
            </GridBox4>
            <hr style={{ marginBottom: "0px" }} />
            {newTeardownTimeIds.map((newTeardownTimeId) => (
              <CreateNewTeardownTime
                key={newTeardownTimeId}
                newTeardownTimeId={newTeardownTimeId}
                onRemoveNewTeardownTimeId={handleRemoveNewTeardownTimeId}
              />
            ))}
            {data.teardownTimes.map((teardownTime) => (
              <TeardownTime key={teardownTime.id} onDelete={handleDelete} teardownTime={teardownTime} />
            ))}
          </>
        )}
      </Box>
    );
  }

  return (
    <PageWithBreadcrumbs
      className="spec-teardown-time-create-edit-page"
      data-testid="teardown-page"
      description=" Manage your site’s teardown times on this page. Once a teardown time is created, you will have the option to
        apply it to scheduled work blocks."
      title="Teardown times"
    >
      {renderContent()}
    </PageWithBreadcrumbs>
  );
}
